import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, User, FileText, Upload, Calendar, Phone, Mail, MapPin,
  File, Download, Trash2, Plus, Clock, CheckCircle, AlertCircle,
  ChevronDown, ChevronUp, Eye, Edit, Send, Folder, Activity, MessageCircle, X, Braces
} from 'lucide-react';

import API_BASE_URL from '../config/api';
import { useCurrentPatient } from '../contexts/CurrentPatientContext';
import { useClinic } from '../contexts/ClinicContext';
import { useAuth } from '../contexts/AuthContext';
const API_URL = API_BASE_URL;

function PatientFilePage() {
  const { setCurrentPatient } = useCurrentPatient();
  const { user } = useAuth();
  const isDoctor = user?.role === 'doctor' || user?.role === 'admin';
  const [generating, setGenerating] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('imaging');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadDate, setUploadDate] = useState('');
  const [uploadSource, setUploadSource] = useState('');

  // Q&A chat state
  const [chatOpen, setChatOpen] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Download the generated ACC impairment report for a processed referral.
  const downloadAccReport = async (referral) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/acc-assessments/${referral.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name = (patient?.name || 'patient').replace(/\s+/g, '_');
      a.download = `ACC_Assessment_${name}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('ACC report download failed:', err);
      alert('Could not download the report. It may not have been generated for this referral.');
    }
  };

  const openChat = (referralId) => {
    setChatOpen(referralId);
    setChatMessages([]);
    setChatInput('');
  };

  const closeChat = () => {
    setChatOpen(null);
    setChatMessages([]);
    setChatInput('');
  };

  const sendQuestion = async () => {
    const question = chatInput.trim();
    if (!question || chatLoading || !chatOpen) return;

    const newMessages = [...chatMessages, { role: 'user', content: question }];
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/acc-assessments/${chatOpen}/ask`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ question, history: chatMessages })
      });

      const data = await response.json();
      if (!response.ok) {
        setChatMessages([...newMessages, { role: 'assistant', content: `Error: ${data.error || 'Failed to get answer'}` }]);
      } else {
        setChatMessages([...newMessages, { role: 'assistant', content: data.answer }]);
      }
    } catch (err) {
      setChatMessages([...newMessages, { role: 'assistant', content: `Error: ${err.message}` }]);
    }
    setChatLoading(false);
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatLoading]);

  // Authenticated PDF download helper
  const downloadPdf = async (url, fallbackFilename = 'download.pdf') => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to download PDF');
        return;
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Extract filename from Content-Disposition header if available
      const disposition = response.headers.get('Content-Disposition');
      let filename = fallbackFilename;
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  // Authenticated structured-data (JSON) download helper.
  // Fetches the full form record (clinicalData + metadata) and saves it as a
  // pretty-printed .json file — the structured counterpart to the PDF download.
  const downloadJson = async (url, filename = 'form.json') => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to download form data');
        return;
      }

      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('JSON download error:', err);
      alert('Failed to download form data. Please try again.');
    }
  };

  useEffect(() => {
    fetchPatient();
  }, [id]);

  // Push patient into the global context so the banner reflects the viewed patient
  useEffect(() => {
    if (patient) {
      setCurrentPatient({
        id: patient.id,
        name: patient.name,
        nhi: patient.nhi,
        dateOfBirth: patient.dateOfBirth,
        phone: patient.phone,
        email: patient.email,
        address: patient.address
      });
    }
  }, [patient, setCurrentPatient]);

  const fetchPatient = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/patient-files/${id}`);
      setPatient(response.data);
    } catch (err) {
      console.error('Error fetching patient:', err);
      setError('Failed to load patient file');
    } finally {
      setLoading(false);
    }
  };

  // Trigger consultation letter + MRI referral generation for this patient.
  // Works independently of the dashboard's "Ready for Letters" filter so it
  // can rescue patients without a Referral row (e.g. after a referral was
  // deleted, or when the patient was synced from Gensolve directly).
  const handleGenerateLetters = async () => {
    if (!patient || generating) return;
    if (!window.confirm(`Generate consultation letter and MRI referral for ${patient.name}?`)) return;
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/letters/generate-all/${patient.id}`, { includeMRI: true });
      await fetchPatient();
      setActiveTab('letters');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate letters');
    } finally {
      setGenerating(false);
    }
  };

  // Cancel a booked appointment from the staff side: cancels in Gensolve AND
  // in ClinHx so the two systems stay aligned, instead of leaving an orphaned
  // 'booked' row in ClinHx after a Gensolve-only cancel.
  const [cancelling, setCancelling] = useState(false);
  const handleCancelAppointment = async () => {
    if (!patient || cancelling) return;
    const slotText = patient.selectedSlot
      ? new Date(patient.selectedSlot).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })
      : 'this appointment';
    const reason = window.prompt(`Cancel ${slotText} for ${patient.name}?\n\nOptional: enter a reason (recorded in the activity log). Click OK to cancel, Cancel to abort.`, '');
    if (reason === null) return;
    setCancelling(true);
    try {
      const res = await axios.post(`${API_URL}/booking/cancel-by-staff/${patient.id}`, { reason: reason || undefined });
      const gs = res.data?.gensolveCancelled
        ? 'Gensolve also cancelled.'
        : res.data?.gensolveError
          ? `Note: Gensolve cancel failed (${res.data.gensolveError}) — you may need to cancel manually in Gensolve.`
          : 'No Gensolve appointment was linked, so only ClinHx was updated.';
      alert(`Appointment cancelled.\n${gs}`);
      await fetchPatient();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel appointment');
    } finally {
      setCancelling(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('document', uploadFile);
    formData.append('name', uploadName || uploadFile.name);
    formData.append('category', uploadCategory);
    formData.append('description', uploadDescription);
    formData.append('documentDate', uploadDate);
    formData.append('source', uploadSource);

    try {
      await axios.post(`${API_URL}/patient-files/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Reset form and refresh
      setUploadModalOpen(false);
      setUploadFile(null);
      setUploadName('');
      setUploadDescription('');
      setUploadDate('');
      setUploadSource('');
      fetchPatient();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId, documentName) => {
    if (!window.confirm(`Are you sure you want to delete "${documentName}"?`)) return;

    try {
      await axios.delete(`${API_URL}/patient-files/${id}/documents/${documentId}`);
      fetchPatient();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete document');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      imaging: { bg: '#dbeafe', text: '#1e40af', label: 'Imaging' },
      reports: { bg: '#dcfce7', text: '#166534', label: 'Reports' },
      correspondence: { bg: '#fef3c7', text: '#92400e', label: 'Correspondence' },
      referrals: { bg: '#e9d5ff', text: '#7c3aed', label: 'Referrals' },
      other: { bg: '#f3f4f6', text: '#374151', label: 'Other' }
    };
    return colors[category] || colors.other;
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: '#dcfce7', text: '#166534', icon: CheckCircle },
      pending: { bg: '#fef3c7', text: '#92400e', icon: Clock },
      draft: { bg: '#f3f4f6', text: '#374151', icon: Edit },
      approved: { bg: '#dbeafe', text: '#1e40af', icon: CheckCircle },
      pending_review: { bg: '#fef3c7', text: '#92400e', icon: AlertCircle }
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280' }}>Loading patient file...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <AlertCircle style={{ width: '64px', height: '64px', color: '#dc2626', margin: '0 auto 16px' }} />
          <h2 style={{ color: '#111827', marginBottom: '8px' }}>Error Loading Patient</h2>
          <p style={{ color: '#6b7280' }}>{error || 'Patient not found'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ marginTop: '24px', padding: '12px 24px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '16px' }}
          >
            <ArrowLeft style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Back to Dashboard
          </button>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '64px', height: '64px', backgroundColor: '#2563eb', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <User style={{ width: '32px', height: '32px', color: 'white' }} />
              </div>
              <div>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>{patient.name}</h1>
                <div style={{ display: 'flex', gap: '16px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {patient.nhi && <span style={{ color: '#6b7280', fontSize: '14px' }}>NHI: {patient.nhi}</span>}
                  {patient.dateOfBirth && <span style={{ color: '#6b7280', fontSize: '14px' }}>DOB: {patient.dateOfBirth}</span>}
                  {patient.accNumber && <span style={{ color: '#6b7280', fontSize: '14px' }}>ACC: {patient.accNumber}</span>}
                  <select
                    value={patient.clinicId || 'cambridge-sports-medicine'}
                    onChange={async (e) => {
                      const newClinicId = e.target.value;
                      try {
                        await axios.patch(`${API_URL}/patients/${id}/clinic`, { clinicId: newClinicId });
                        setPatient(prev => ({ ...prev, clinicId: newClinicId }));
                      } catch (err) {
                        console.error('Failed to change clinic:', err);
                        alert('Failed to change clinic: ' + (err.response?.data?.error || err.message));
                      }
                    }}
                    style={{ fontSize: '12px', padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db', backgroundColor: patient.clinicId === 'anglesea-sports-medicine' ? '#EF6C00' : '#1976D2', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <option value="cambridge-sports-medicine">CAM</option>
                    <option value="anglesea-sports-medicine">HAM</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Show Cancel Appointment when there's something to cancel: a
                  confirmed/booked status, a future selectedSlot, or a Gensolve
                  appointment ID hanging around. */}
              {(patient.gensolveAppointmentId || ['confirmed', 'booked'].includes(patient.appointmentStatus) || patient.selectedSlot) && !patient.cancelledAt && (
                <button
                  onClick={handleCancelAppointment}
                  disabled={cancelling}
                  title="Cancel this booked appointment in both ClinHx and Gensolve"
                  style={{
                    display: 'flex', alignItems: 'center', padding: '10px 16px',
                    backgroundColor: cancelling ? '#9ca3af' : '#dc2626',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: cancelling ? 'not-allowed' : 'pointer', fontWeight: '500'
                  }}
                >
                  <X style={{ width: '18px', height: '18px', marginRight: '6px' }} />
                  {cancelling ? 'Cancelling…' : 'Cancel Appointment'}
                </button>
              )}
              <button
                onClick={() => setUploadModalOpen(true)}
                style={{ display: 'flex', alignItems: 'center', padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
              >
                <Upload style={{ width: '20px', height: '20px', marginRight: '8px' }} />
                Upload Document
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>Referrals</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{patient.referrals?.length || 0}</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>Forms</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{patient.forms?.length || 0}</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>Documents</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{patient.documents?.length || 0}</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>Letters</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{patient.letters?.length || 0}</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', overflowX: 'auto' }}>
          {['overview', 'documents', 'forms', 'letters', 'timeline'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 16px',
                backgroundColor: activeTab === tab ? '#2563eb' : 'transparent',
                color: activeTab === tab ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '24px' }}>
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
              {/* Contact Information */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>Contact Information</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {patient.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Phone style={{ width: '18px', height: '18px', color: '#6b7280' }} />
                      <span>{patient.phone}</span>
                    </div>
                  )}
                  {patient.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Mail style={{ width: '18px', height: '18px', color: '#6b7280' }} />
                      <span>{patient.email}</span>
                    </div>
                  )}
                  {patient.address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <MapPin style={{ width: '18px', height: '18px', color: '#6b7280' }} />
                      <span>{patient.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Consent Status */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>Consent Status</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(() => {
                    const aiConsent = patient.consents?.find(c => c.consentType === 'ai_technology');
                    const hasAIConsent = aiConsent ? aiConsent.given : patient.consentAI;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', backgroundColor: hasAIConsent ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', border: `1px solid ${hasAIConsent ? '#bbf7d0' : '#fecaca'}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {hasAIConsent
                            ? <CheckCircle style={{ width: '18px', height: '18px', color: '#16a34a' }} />
                            : <AlertCircle style={{ width: '18px', height: '18px', color: '#dc2626' }} />}
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>AI Technology</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{hasAIConsent ? 'Consent granted' : 'Consent declined'}</div>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            const action = hasAIConsent ? 'revoke' : 'grant';
                            if (!window.confirm(`Are you sure you want to ${action} AI consent for this patient? This should only be done with a signed consent form.`)) return;
                            try {
                              await axios.put(`${API_URL}/patient-files/${id}/consent/ai`, { given: !hasAIConsent });
                              fetchPatient();
                            } catch (err) {
                              alert('Failed to update consent: ' + (err.response?.data?.error || err.message));
                            }
                          }}
                          style={{
                            padding: '6px 14px', fontSize: '13px', fontWeight: '500', borderRadius: '6px', cursor: 'pointer',
                            border: 'none', color: 'white',
                            backgroundColor: hasAIConsent ? '#dc2626' : '#16a34a'
                          }}
                        >
                          {hasAIConsent ? 'Revoke' : 'Grant'}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>Recent Activity</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {patient.activityLogs?.slice(0, 5).map((log, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                      <Activity style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                      <div>
                        <div style={{ fontSize: '14px', color: '#111827' }}>{log.description}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatDate(log.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                  {(!patient.activityLogs || patient.activityLogs.length === 0) && (
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No recent activity</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Documents</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API_URL}/patient-files/${id}/sync-referrer-docs`);
                        alert(res.data.message || 'Sync complete');
                        fetchPatient();
                      } catch (err) {
                        alert(err.response?.data?.error || 'Failed to sync referrer documents');
                      }
                    }}
                    style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                  >
                    Import Referral Docs
                  </button>
                  <button
                    onClick={() => setUploadModalOpen(true)}
                    style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                  >
                    <Plus style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                    Add Document
                  </button>
                </div>
              </div>
              
              {patient.documents?.length > 0 ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {patient.documents.map((doc) => {
                    const catStyle = getCategoryColor(doc.category);
                    return (
                      <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <File style={{ width: '24px', height: '24px', color: '#6b7280' }} />
                          <div>
                            <div style={{ fontWeight: '500', color: '#111827' }}>{doc.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {formatDate(doc.uploadedAt)} {doc.source && `• ${doc.source}`}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ padding: '4px 12px', backgroundColor: catStyle.bg, color: catStyle.text, borderRadius: '9999px', fontSize: '12px', fontWeight: '500' }}>
                            {catStyle.label}
                          </span>
                          <button
                            onClick={async () => {
                              if (doc.downloadUrl) {
                                window.open(doc.downloadUrl, '_blank');
                              } else {
                                try {
                                  const res = await axios.get(`${API_URL}/patient-files/${id}/documents/${doc.id}/download`);
                                  if (res.data.downloadUrl) {
                                    window.open(res.data.downloadUrl, '_blank');
                                  } else {
                                    alert('No file available for download');
                                  }
                                } catch (err) {
                                  const msg = err.response?.data?.error || 'Failed to get download link';
                                  if (window.confirm(`${msg}\n\nWould you like to remove this document record?`)) {
                                    try {
                                      await axios.delete(`${API_URL}/patient-files/${id}/documents/${doc.id}`);
                                      fetchPatient();
                                    } catch (delErr) {
                                      alert('Failed to delete document record');
                                    }
                                  }
                                }
                              }
                            }}
                            style={{ padding: '8px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <Download style={{ width: '18px', height: '18px' }} />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc.id, doc.name)}
                            style={{ padding: '8px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <Trash2 style={{ width: '18px', height: '18px' }} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <Folder style={{ width: '48px', height: '48px', margin: '0 auto 16px', opacity: 0.5 }} />
                  <p>No documents uploaded yet</p>
                  <button
                    onClick={() => setUploadModalOpen(true)}
                    style={{ marginTop: '16px', padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    Upload First Document
                  </button>
                </div>
              )}

              {/* Referrals with Q&A */}
              {patient.referrals?.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Referral Documents</h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {patient.referrals.map((referral) => (
                      <div key={referral.id} style={{ padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <FileText style={{ width: '24px', height: '24px', color: '#7c3aed' }} />
                            <div>
                              <div style={{ fontWeight: '500', color: '#111827' }}>{referral.fileName || 'Referral'}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {referral.injuryType} {referral.referrerName && `• ${referral.referrerName}`} • {formatDate(referral.uploadedAt)}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ padding: '4px 12px', backgroundColor: '#e9d5ff', color: '#7c3aed', borderRadius: '9999px', fontSize: '12px', fontWeight: '500' }}>
                              {referral.status}
                            </span>
                            {/* ACC impairment assessments: reach the generated
                                report and the stage-2 analysis from the patient
                                record, not only from the upload session. */}
                            {String(referral.injuryType || '').startsWith('ACC_') && referral.status === 'processed' && (
                              <>
                                <button
                                  onClick={() => downloadAccReport(referral)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '8px 14px', backgroundColor: '#2563eb',
                                    color: 'white', border: 'none', borderRadius: '8px',
                                    cursor: 'pointer', fontSize: '13px', fontWeight: '500'
                                  }}
                                >
                                  <Download style={{ width: '14px', height: '14px' }} />
                                  Download Report
                                </button>
                                <button
                                  onClick={() => navigate(`/impairment/analysis/${referral.id}`)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '8px 14px', backgroundColor: '#4f46e5',
                                    color: 'white', border: 'none', borderRadius: '8px',
                                    cursor: 'pointer', fontSize: '13px', fontWeight: '500'
                                  }}
                                >
                                  <Activity style={{ width: '14px', height: '14px' }} />
                                  History &amp; Examination
                                </button>
                              </>
                            )}
                            {referral.hasDocumentText && (
                              <button
                                onClick={() => chatOpen === referral.id ? closeChat() : openChat(referral.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '8px 14px',
                                  backgroundColor: chatOpen === referral.id ? '#dc2626' : '#059669',
                                  color: 'white', border: 'none', borderRadius: '8px',
                                  cursor: 'pointer', fontSize: '13px', fontWeight: '500'
                                }}
                              >
                                <MessageCircle style={{ width: '14px', height: '14px' }} />
                                {chatOpen === referral.id ? 'Close Q&A' : 'Ask Questions'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Q&A Chat Panel */}
                  {chatOpen && (
                    <div style={{ marginTop: '20px', border: '2px solid #059669', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: '#059669', color: '#fff' }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>Document Q&A</h4>
                        <button onClick={closeChat} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}>X</button>
                      </div>

                      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}>
                        {chatMessages.length === 0 && (
                          <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px', fontSize: '14px' }}>
                            <p style={{ marginBottom: '8px' }}>Ask questions about the uploaded referral documents.</p>
                            <p style={{ fontSize: '13px', fontStyle: 'italic' }}>
                              e.g., "What injuries are documented?", "What was the mechanism of injury?", "Summarise the clinical findings"
                            </p>
                          </div>
                        )}
                        {chatMessages.map((msg, idx) => (
                          <div key={idx} style={{
                            marginBottom: '12px', padding: '10px 14px', borderRadius: '8px',
                            backgroundColor: msg.role === 'user' ? '#eff6ff' : '#f0fdf4',
                            borderLeft: `3px solid ${msg.role === 'user' ? '#2563eb' : '#059669'}`
                          }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px' }}>
                              {msg.role === 'user' ? 'You' : 'AI Assistant'}
                            </div>
                            <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                              {msg.content}
                            </div>
                          </div>
                        ))}
                        {chatLoading && (
                          <div style={{ padding: '10px 14px', color: '#6b7280', fontStyle: 'italic', fontSize: '14px' }}>Thinking...</div>
                        )}
                        <div ref={chatEndRef} />
                      </div>

                      <div style={{ display: 'flex', gap: '8px', padding: '12px 16px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                        <textarea
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={handleChatKeyDown}
                          placeholder="Ask a question about the documents..."
                          rows={1}
                          style={{ flex: 1, padding: '10px 12px', border: '2px solid #d1d5db', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'none', outline: 'none' }}
                        />
                        <button
                          onClick={sendQuestion}
                          disabled={chatLoading || !chatInput.trim()}
                          style={{
                            padding: '10px 20px', backgroundColor: chatLoading || !chatInput.trim() ? '#9ca3af' : '#059669',
                            color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                            cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer', alignSelf: 'flex-end'
                          }}
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Forms Tab */}
          {activeTab === 'forms' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Completed Forms</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => downloadPdf(`${API_URL}/patients/${id}/consent/pdf`, `Consent_${patient.name}.pdf`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    <Download style={{ width: '14px', height: '14px' }} />
                    Consent PDF
                  </button>
                  <button
                    onClick={() => downloadPdf(`${API_URL}/patients/${id}/questionnaire/pdf`, `Questionnaire_${patient.name}.pdf`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    <Download style={{ width: '14px', height: '14px' }} />
                    Questionnaire PDF
                  </button>
                </div>
              </div>
              {patient.forms?.length > 0 ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {patient.forms.map((form) => {
                    const statusStyle = getStatusBadge(form.status);
                    const StatusIcon = statusStyle.icon;
                    return (
                      <div key={form.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <FileText style={{ width: '24px', height: '24px', color: '#6b7280' }} />
                          <div>
                            <div style={{ fontWeight: '500', color: '#111827' }}>{form.template} - {form.formType}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatDate(form.createdAt)}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', padding: '4px 12px', backgroundColor: statusStyle.bg, color: statusStyle.text, borderRadius: '9999px', fontSize: '12px', fontWeight: '500' }}>
                            <StatusIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            {form.status}
                          </span>
                          <button
                            onClick={() => downloadPdf(`${API_URL}/patients/${id}/forms/${form.id}/pdf`, `${form.template}_${patient.name}.pdf`)}
                            style={{ padding: '8px', color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer' }}
                            title="Download Form PDF"
                          >
                            <Download style={{ width: '18px', height: '18px' }} />
                          </button>
                          <button
                            onClick={() => downloadJson(`${API_URL}/patients/${id}/forms/${form.id}/json`, `${form.template}_${patient.name}.json`)}
                            style={{ padding: '8px', color: '#0f766e', background: 'none', border: 'none', cursor: 'pointer' }}
                            title="Download Form Data (JSON)"
                          >
                            <Braces style={{ width: '18px', height: '18px' }} />
                          </button>
                          <button style={{ padding: '8px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}>
                            <Eye style={{ width: '18px', height: '18px' }} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>No forms completed yet</p>
              )}
            </div>
          )}

          {/* Letters Tab */}
          {activeTab === 'letters' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Generated Letters</h3>
                {isDoctor && patient.formCompleted && (
                  <button
                    onClick={handleGenerateLetters}
                    disabled={generating}
                    title="Generate consultation letter + MRI referral from the completed form"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 14px',
                      backgroundColor: generating ? '#9ca3af' : '#7c3aed',
                      color: 'white', border: 'none', borderRadius: '8px',
                      cursor: generating ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500'
                    }}
                  >
                    <FileText style={{ width: '14px', height: '14px' }} />
                    {generating ? 'Generating…' : (patient.letters?.length > 0 ? 'Regenerate Letters' : 'Generate Letters')}
                  </button>
                )}
              </div>
              {patient.letters?.length > 0 ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {patient.letters.map((letter) => {
                    const statusStyle = getStatusBadge(letter.status);
                    const StatusIcon = statusStyle.icon;
                    return (
                      <div key={letter.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <FileText style={{ width: '24px', height: '24px', color: '#2563eb' }} />
                          <div>
                            <div style={{ fontWeight: '500', color: '#111827' }}>{letter.letterType} Consultation Letter</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatDate(letter.generatedAt)}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', padding: '4px 12px', backgroundColor: statusStyle.bg, color: statusStyle.text, borderRadius: '9999px', fontSize: '12px', fontWeight: '500' }}>
                            <StatusIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            {letter.status.replace('_', ' ')}
                          </span>
                          <button
                            onClick={() => navigate(`/letters?letterId=${letter.id}`)}
                            style={{ padding: '8px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <Eye style={{ width: '18px', height: '18px' }} />
                          </button>
                          {letter.downloadUrl && (
                            <a href={letter.downloadUrl} target="_blank" rel="noopener noreferrer" style={{ padding: '8px', color: '#2563eb' }}>
                              <Download style={{ width: '18px', height: '18px' }} />
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>No letters generated yet</p>
              )}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Activity Timeline</h3>
              {patient.activityLogs?.length > 0 ? (
                <div style={{ position: 'relative', paddingLeft: '24px' }}>
                  <div style={{ position: 'absolute', left: '8px', top: '0', bottom: '0', width: '2px', backgroundColor: '#e5e7eb' }} />
                  {patient.activityLogs.map((log, idx) => (
                    <div key={idx} style={{ position: 'relative', paddingBottom: '24px' }}>
                      <div style={{ position: 'absolute', left: '-20px', width: '16px', height: '16px', backgroundColor: '#2563eb', borderRadius: '50%', border: '3px solid white' }} />
                      <div style={{ backgroundColor: '#f9fafb', padding: '12px 16px', borderRadius: '8px' }}>
                        <div style={{ fontWeight: '500', color: '#111827' }}>{log.description}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          {formatDate(log.createdAt)} {log.user?.name && `• ${log.user.name}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>No activity recorded</p>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '24px' }}>Upload Document</h2>
            
            <form onSubmit={handleFileUpload}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>File *</label>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.jpg,.jpeg,.png"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                  required
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Document Name</label>
                <input
                  type="text"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                  placeholder="e.g., MRI Report - Right Knee"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Category *</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                  required
                >
                  <option value="imaging">Imaging (MRI, X-ray, CT, Ultrasound)</option>
                  <option value="reports">Reports (Specialist, Lab)</option>
                  <option value="correspondence">Correspondence</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Document Date</label>
                <input
                  type="date"
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Source</label>
                <input
                  type="text"
                  value={uploadSource}
                  onChange={(e) => setUploadSource(e.target.value)}
                  placeholder="e.g., Waikato Radiology, Anglesea Clinic"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Description</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optional notes about this document"
                  rows={3}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  disabled={uploading || !uploadFile}
                  style={{ flex: 1, padding: '12px', backgroundColor: uploading ? '#9ca3af' : '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: '500' }}
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
                <button
                  type="button"
                  onClick={() => setUploadModalOpen(false)}
                  style={{ flex: 1, padding: '12px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientFilePage;
